import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { clearSession } from './session';
import { installAxiosFailover } from '../config/apiFailover';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000,
});

// Conmutación de emergencia del host de la API (config/apiFailover.js): si api.tuplazaia.com
// no responde pero el dominio de Railway sí, las peticiones cambian de host y se reintentan.
installAxiosFailover(apiClient);

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('session_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add CSRF token to mutating requests
apiClient.interceptors.request.use((config) => {
  if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
    const csrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrf_token='))
      ?.split('=')[1];
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  return config;
});

// Redirige a /auth conservando la ruta actual (?next=...) para que, tras
// iniciar sesión, el usuario vuelva a donde estaba (p. ej. /results/exam_XXXX
// al recargar la página de resultados) en vez de acabar en /dashboard.
const redirectToAuth = () => {
  if (window.location.pathname.startsWith('/auth')) return;
  const next = window.location.pathname + window.location.search;
  const nextParam = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : '';
  window.location.href = `/auth${nextParam}`;
};

// ==================== Refresh Token Rotation ====================
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor - handle 401 with token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh on 401, and not for the refresh endpoint itself
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes('/api/auth/refresh')
    ) {
      // If it's a non-refreshable 401, clear tokens and redirect
      if (error.response?.status === 401 && (originalRequest._retry || originalRequest.url?.includes('/api/auth/refresh'))) {
        // clearSession también borra el usuario cacheado y purga la caché de API del SW
        clearSession();
        redirectToAuth();
      }
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      // No refresh token available, redirect to auth
      clearSession();
      redirectToAuth();
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
        refresh_token: refreshToken,
      });

      const { session_token: newToken, refresh_token: newRefreshToken } = response.data;

      localStorage.setItem('session_token', newToken);
      localStorage.setItem('refresh_token', newRefreshToken);

      processQueue(null, newToken);

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);

      clearSession();
      redirectToAuth();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

/**
 * Normalize question data to handle inconsistent field names from the API.
 * Ensures both "text" and "question_text" variants are accessible via a
 * consistent "text" property, and likewise for option text.
 */
export const normalizeQuestion = (q) => ({
  ...q,
  text: q.text || q.question_text,
  options: q.options?.map(o =>
    typeof o === 'string'
      ? o
      : { ...o, text: o.text || o.option_text }
  ),
});

export const normalizeQuestions = (questions) =>
  Array.isArray(questions) ? questions.map(normalizeQuestion) : [];

export default apiClient;
